import { all, call, fork, put, select, take } from 'redux-saga/effects';

import { message } from 'antd';
import { createMatchSelector, push } from 'connected-react-router';
import { updateNoteToCard } from 'containers/MainLayout/actions';
import translations from 'translations';
import {
  ProductSortField,
  QueryGetBrandItemsInSameProductGroupsArgs,
  QueryProductSearchArgs,
  SortDirection,
} from 'types/schema';
import {
  addFavorite,
  getBrand,
  getBrandItemsInSameProductGroupsApi,
  getBrands,
  getBrandsRelatedToCategory,
  getCategoriesRelatedToBrand,
  getProducts,
  removeFavorite,
  setProductNote,
} from 'utils/apollo';
import { LANGUAGES } from 'utils/constants';
import { generateUrlBrandDetail } from 'utils/generateBrandDetailUrl';
import utilMessages from 'utils/messages';
import {
  brandDetail,
  brandDetailFavorite,
  brandDetailSetProductNote,
  brandProducts,
  getKamereoItemsInSameProductGroups,
  kamereoItemFavorite,
  kamereoItemSetNote,
  similarBrands,
} from './actions';
import ActionTypes from './constants';
import { selectBrandInfo, selectCategoryIds } from './selectors';
import { selectBuyer } from 'containers/MainLayout/selectors';

function* getBrandProducts({
  brandId,
  categoryIds,
  brandName,
  brandLogo,
  page = 0,
  isMainBrand = false,
}: {
  brandId?: number;
  categoryIds?: number[];
  brandName?: string;
  brandLogo?: string;
  page?: number;
  isMainBrand?: boolean;
}) {
  const brandInfo = yield select(selectBrandInfo());
  let currentBrandId = brandInfo.id;

  if (brandId) {
    currentBrandId = brandId;
  }

  yield put(
    brandProducts.request({
      [currentBrandId]: {
        id: currentBrandId,
        name: brandId ? brandName : brandInfo.name,
        logo: brandId ? brandLogo : brandInfo.logo,
        page: page,
        data: !!page ? undefined : [],
        loading: true,
      },
    }),
  );

  try {
    const params: QueryProductSearchArgs = {
      sort: [{ field: ProductSortField.Name, order: SortDirection.Asc }],
      filter: {
        brandId: Number(currentBrandId),
        categoryIds: isMainBrand ? categoryIds || [] : [],
      },
      pagination: { page: page || 0, size: 20 },
    };
    const response = yield call(getProducts, params);
    if (!response.errors) {
      yield put(
        brandProducts.success({
          [currentBrandId]: {
            id: currentBrandId,
            name: brandId ? brandName : brandInfo.name,
            logo: brandId ? brandLogo : brandInfo.logo,
            data: response.data,
            page: page,
            totalResults: response.totalResults,
            loading: false,
          },
        }),
      );
    }
  } catch (error) {
    yield put(brandProducts.failure(error as Error));
  }
}

function* getKamereoProductsInSameProductGroup(brandId: number) {
  const buyer = yield select(selectBuyer());
  const region = window.localStorage.getItem('region') || 'HCM';
  try {
    const params: QueryGetBrandItemsInSameProductGroupsArgs = {
      brandId: brandId,
      regionCode: buyer?.id ? buyer.region : region,
      pagination: { page: 0, size: 20 },
    };
    const response = yield call(getBrandItemsInSameProductGroupsApi, params);
    if (!response.errors) {
      yield put(
        getKamereoItemsInSameProductGroups.success({
          [brandId]: {
            brandId: brandId,
            products: response?.data,
          },
        }),
      );
    }
  } catch (error) {
    yield put(
      getKamereoItemsInSameProductGroups.failure({
        [brandId]: {
          brandId: brandId,
          products: [],
        },
      }),
    );
  }
}

function* getGroupBrands(groupName: string, brandId: string) {
  const response = yield call(getBrands, {
    groupName: groupName,
    pagination: { page: 0, size: 1000 },
  });

  if (!response.errors) {
    const brands = response?.data;
    yield all(
      brands.map((item) => {
        if (brandId === item.id) return;
        return call(getBrandProducts, {
          brandId: item.id,
          brandName: item.name,
          brandLogo: item.imageUrl,
        });
      }),
    );
    yield put(getKamereoItemsInSameProductGroups.request());
    yield all(
      brands.map((item) => {
        if (brandId === item.id) return;
        return call(getKamereoProductsInSameProductGroup, Number(item.id));
      }),
    );
  }
}

function* getListOfCategories(brandId: number) {
  const brandSubCategories = yield call(getCategoriesRelatedToBrand, {
    filter: {
      brandIds: [Number(brandId)],
    },
    pagination: { page: 0, size: 1000 },
  });

  if (!brandSubCategories.errors) {
    return brandSubCategories.data;
  }
  return [];
}

function* filterFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.APPLY_FILTER);
    yield call(getBrandProducts, {
      categoryIds: payload.categoryId ? [payload.categoryId] : undefined,
      isMainBrand: true,
    });
  }
}

function* viewMoreFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.VIEW_MORE);
    let categoryIds = [];
    if (payload.isMainBrand) {
      categoryIds = yield select(selectCategoryIds());
    }
    yield call(getBrandProducts, {
      brandId: payload.brandId,
      page: payload.page,
      categoryIds: categoryIds,
      isMainBrand: payload.isMainBrand,
    });
  }
}

function* getSimilarBrands(categories: number[], brandId: string) {
  const response = yield call(getBrandsRelatedToCategory, {
    filter: {
      categoryIds: categories,
    },
    pagination: { page: 0, size: 1000 },
  });

  if (!response.errors) {
    const brands = response?.data?.filter((brand) => brand.id !== brandId);
    yield put(similarBrands.success(brands));
  } else {
    yield put(similarBrands.failure(response.errors));
  }
}

const replaceLinksWithAnchorTags = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return text.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank">${url}</a>`;
  });
};
function* initBrandDetails() {
  // const localLang = yield select(selectLang());
  const localLang = localStorage.getItem('lang') || LANGUAGES.Vi;
  const alternateLang = localLang === LANGUAGES.Vi ? LANGUAGES.Alternate : LANGUAGES.Vi;
  yield put(brandDetail.request());

  const route = yield select(createMatchSelector('/thuong-hieu/:slug'));
  const slugs = route.params.slug;
  const slugsArray = slugs.split('-');
  const id = slugsArray[slugsArray.length - 1].slice(1);

  const response = yield call(getBrand, { brandId: Number(id) });

  if (!response.errors) {
    window.history.replaceState('', '', `${generateUrlBrandDetail(response.id, response.name, localLang)}`);
    const refinedCategories = yield call(getListOfCategories, response.id);
    yield put(
      brandDetail.success({
        brandInfo: {
          id: response.id,
          logo: response.imageUrl,
          name: response.name,
          description:
            localLang === 'vi'
              ? replaceLinksWithAnchorTags(response.description_vi)
              : replaceLinksWithAnchorTags(response.description),
          groupName: response.groupName,
          status: response.status,
        },
        localLang: localLang,
        alternateLang: alternateLang,
        categories: refinedCategories,
        brandSection: {
          [response.id]: { id: response.id, name: response.name, logo: response.imageUrl, data: [], loading: true },
        },
      }),
    );
    yield fork(getKamereoProductsInSameProductGroup, Number(response.id));
    yield all([
      call(getBrandProducts, {
        brandId: response.id,
      }),
      response.groupName && call(getGroupBrands, response.groupName, response.id),
      call(
        getSimilarBrands,
        refinedCategories.map((category) => category.id),
        id,
      ),
    ]);
  } else {
    yield put(brandDetail.failure(response.errors));
    yield put(push('/not-found'));
  }
}

function* brandDetailFavoriteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.SET_FAVORITE);
    yield put(brandDetailFavorite.request());

    const {
      product: { isFavorite, id },
      index,
      section,
    } = payload;

    const response = yield call(isFavorite ? removeFavorite : addFavorite, { id: id });
    if (response.errors) {
      yield put(brandDetailFavorite.failure({ index: index, section: section }));
    } else {
      yield put(brandDetailFavorite.success({ index: index, section: section, isFavorite: !isFavorite }));
    }
  }
}

function* kamereoItemFavoriteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.SET_KAMEREO_ITEM_FAVORITE_REQUEST);
    const {
      product: { isFavorite, id },
      index,
      brandId,
    } = payload;

    const response = yield call(isFavorite ? removeFavorite : addFavorite, { id: id });
    if (response.errors) {
      yield put(kamereoItemFavorite.failure({ index: index, brandId: brandId }));
    } else {
      yield put(kamereoItemFavorite.success({ index: index, brandId: brandId, isFavorite: !isFavorite }));
    }
  }
}

function* brandDetailSetProductNoteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.SET_PRODUCT_NOTE);
    yield put(brandDetailSetProductNote.request());

    const { id, note, index, section } = payload;
    const response = yield call(setProductNote, { input: { productId: id, note: note } });
    if (response.errors) {
      yield put(brandDetailSetProductNote.failure(response.errors));
      message.error(translations(utilMessages[payload.note ? 'addItemNoteFailed' : 'deleteItemNoteFailed']));
    } else {
      yield put(brandDetailSetProductNote.success({ note: note, index: index, section: section }));
      yield put(updateNoteToCard(response.product));
      message.success(translations(utilMessages[payload.note ? 'addItemNoteSuccess' : 'deleteItemNoteSuccess']));
    }
  }
}

function* kamereoItemSetNoteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.SET_KAMEREO_NOTE_REQUEST);

    const { product, index, brandId } = payload;
    const response = yield call(setProductNote, { input: { productId: product?.id, note: product?.note } });
    if (response.errors) {
      yield put(kamereoItemSetNote.failure(response.errors));
      message.error(translations(utilMessages[payload.product.note ? 'addItemNoteFailed' : 'deleteItemNoteFailed']));
    } else {
      yield put(kamereoItemSetNote.success({ product: product, index: index, brandId: brandId }));
      message.success(
        translations(utilMessages[payload.product.note ? 'addItemNoteSuccess' : 'deleteItemNoteSuccess']),
      );
    }
  }
}

export default function* saga() {
  yield fork(initBrandDetails);
  yield fork(filterFlow);
  yield fork(viewMoreFlow);
  yield fork(brandDetailFavoriteFlow);
  yield fork(brandDetailSetProductNoteFlow);
  yield fork(kamereoItemFavoriteFlow);
  yield fork(kamereoItemSetNoteFlow);
}
